import React, { useState, useEffect } from 'react';
import {
	Select,
	Tooltip,
	Row,
	Col,
	Form,
	Button,
	Spin,
	message,
	notification,
	Tag,
	Space,
} from 'antd';
import {
	CalendarOutlined,
	ClockCircleOutlined,
	FieldTimeOutlined,
	QuestionCircleOutlined,
	SaveOutlined,
} from '@ant-design/icons';
import momentTimeZone from 'moment-timezone';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getSchedule, updateSchedule, updateScheduleStatus } from '@/api/novi/schedule';
import { getSelectedAgentId } from '@/store/account/selector';

interface ScheduleFormData {
	timezone: string;
	scheduleType: 'business' | 'extended' | 'custom';
	maxCallsPerDay: number;
	selectedDays?: string[];
	startTime?: string;
	endTime?: string;
	hoursBetweenRetries?: number;
	maximumRetries?: number;
}

const defaultFormData: ScheduleFormData = {
	timezone: '',
	scheduleType: 'business',
	maxCallsPerDay: 25,
	selectedDays: [],
	startTime: '09:00',
	endTime: '18:00',
	hoursBetweenRetries: 0,
	maximumRetries: 0,
};

const ScheduleSettings = () => {
	const [formData, setFormData] = useState<ScheduleFormData>(defaultFormData);
	const [initialFormData, setInitialFormData] = useState<ScheduleFormData>(defaultFormData);
	const [isFormChanged, setIsFormChanged] = useState(false);

	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const {
		data: campaignSchedule,
		isLoading,
		refetch,
		isRefetching,
	} = useQuery({
		queryKey: ['campaign-schedule', selectedAgentId],
		queryFn: () => getSchedule(selectedAgentId as string),
		enabled: !!selectedAgentId,
		retry: 2,
		staleTime: 30000,
	});

	useEffect(() => {
		if (campaignSchedule) {
			let scheduleType: 'business' | 'extended' | 'custom' = 'custom';

			if (
				campaignSchedule?.schedule?.allowed_times_start === '09:00' &&
				campaignSchedule?.schedule?.allowed_times_end === '17:00'
			) {
				scheduleType = 'business';
			} else if (
				campaignSchedule?.schedule?.allowed_times_start === '08:00' &&
				campaignSchedule?.schedule?.allowed_times_end === '20:00'
			) {
				scheduleType = 'extended';
			}

			// Map days array to day names
			// In the data: Monday is 1, Sunday is 0
			const dayMapping = [
				'Sunday',
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday',
			];
			const selectedDays = campaignSchedule?.schedule?.days
				? campaignSchedule?.schedule?.days.map((day) => dayMapping[parseInt(day)])
				: [];

			const mappedData = {
				timezone: campaignSchedule?.schedule?.timezone || defaultFormData.timezone,
				scheduleType: scheduleType,
				maxCallsPerDay: 25, // Use default since it's not in the provided data
				selectedDays: selectedDays,
				startTime: campaignSchedule?.schedule?.allowed_times_start || defaultFormData.startTime,
				endTime: campaignSchedule?.schedule?.allowed_times_end || defaultFormData.endTime,
				hoursBetweenRetries: campaignSchedule?.schedule?.time_between_retries
					? campaignSchedule?.schedule?.time_between_retries
					: defaultFormData.hoursBetweenRetries,
				maximumRetries: campaignSchedule?.schedule?.allowed_retries
					? campaignSchedule?.schedule?.allowed_retries
					: defaultFormData.maximumRetries,
			};

			setFormData(mappedData);
			setInitialFormData(mappedData);
			setIsFormChanged(false);
			console.log(campaignSchedule);
		}
	}, [campaignSchedule]);

	const [isToggling, setIsToggling] = useState<boolean>(false);

	const { mutate: scheduleStatusUpdate, isPending } = useMutation({
		mutationFn: async (params: { campaign_id: string; status: string }) => {
			setIsToggling(true);
			try {
				await updateScheduleStatus(params);
			} finally {
				setIsToggling(false);
			}
		},
		onSuccess: () => {
			notification.success({
				message: 'Schedule Status Updated',
				description: 'The schedule status has been updated successfully.',
				duration: 3,
			});
			refetch();
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Failed to Update Schedule Status',
				description: error.message || 'An error occurred while updating the schedule status.',
				duration: 4,
			});
			refetch();
		},
	});

	const handleStatusToggle = async () => {
		if (isToggling || !campaignSchedule) return;
		const newStatus =
			campaignSchedule.schedule?.status?.toLowerCase() === 'active' ? 'INACTIVE' : 'ACTIVE';
		scheduleStatusUpdate({
			campaign_id: selectedAgentId || '',
			status: newStatus,
		});
	};

	const renderScheduleControls = () => {
		const isActive = campaignSchedule?.schedule?.status?.toLowerCase() === 'active';

		return (
			<Button
				type={isActive ? 'default' : 'primary'}
				onClick={handleStatusToggle}
				loading={isToggling}
				disabled={isPending}
				style={{
					borderRadius: '6px',
					padding: '4px 12px',
					height: '28px',
					backgroundColor: isActive ? '#f5f5f5' : undefined,
					borderColor: isActive ? '#d9d9d9' : undefined,
				}}
			>
				<Space size={4}>
					<div
						style={{
							width: '8px',
							height: '8px',
							borderRadius: '50%',
							backgroundColor: isActive ? '#52c41a' : '#ff4d4f',
							display: 'inline-block',
							marginRight: '4px',
						}}
					/>
					{isActive ? 'Turn Off Schedule' : 'Turn On Schedule'}
				</Space>
			</Button>
		);
	};

	const renderStatusBadge = (status: string) => {
		const statusConfig = {
			active: { color: '#52c41a', text: 'ACTIVE' },
			inactive: { color: '#ff4d4f', text: 'INACTIVE' },
		};
		const config =
			statusConfig[status?.toLowerCase() as keyof typeof statusConfig] || statusConfig.inactive;
		return (
			<Tag
				color={config.color}
				style={{ padding: '2px 12px', borderRadius: '12px', fontSize: '12px' }}
			>
				{config.text}
			</Tag>
		);
	};

	// Mutation for saving changes
	const mutation = useMutation({
		mutationFn: async (data: ScheduleFormData) => {
			// Map the form data structure to the API expected structure
			const dayMapping = {
				Sunday: 0,
				Monday: 1,
				Tuesday: 2,
				Wednesday: 3,
				Thursday: 4,
				Friday: 5,
				Saturday: 6,
			};

			// Set time ranges based on schedule type
			let startTime = data.startTime;
			let endTime = data.endTime;

			if (data.scheduleType === 'business') {
				startTime = '09:00';
				endTime = '17:00';
			} else if (data.scheduleType === 'extended') {
				startTime = '08:00';
				endTime = '20:00';
			}

			// Map selected days to numbers
			const days =
				data.selectedDays?.map((day) => dayMapping[day as keyof typeof dayMapping]) || [];

			// If business or extended, set days to weekdays (Monday-Friday)
			if (data.scheduleType === 'business' || data.scheduleType === 'extended') {
				days.length = 0; // Clear array
				[1, 2, 3, 4, 5].forEach((day) => days.push(day)); // Add weekdays
			}

			const apiPayload = {
				allowed_retries: Number(data.maximumRetries),
				time_between_retries: Number(data.hoursBetweenRetries),
				allowed_times_start: startTime,
				allowed_times_end: endTime,
				days: days,
				timezone: data.timezone,
			};

			return updateSchedule(selectedAgentId as string, apiPayload);
		},
		onSuccess: () => {
			message.success('Schedule updated successfully');
			refetch();
			setIsFormChanged(false);
		},
		onError: (error) => {
			message.error(
				`Failed to update schedule: ${error instanceof Error ? error.message : 'Unknown error'}`,
			);
		},
	});

	// Check if form has been modified
	useEffect(() => {
		const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
		setIsFormChanged(hasChanged);
	}, [formData, initialFormData]);

	const timezones = momentTimeZone.tz.names();
	const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

	const handleRadioChange = (name: keyof ScheduleFormData, value: string) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prevData) => ({
			...prevData,
			maxCallsPerDay: parseInt(e.target.value, 10),
		}));
	};

	const handleTimeZoneChange = (value: string) => {
		setFormData((prevData) => ({
			...prevData,
			timezone: value,
		}));
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleDaySelection = (selectedDays: string[]) => {
		setFormData((prevData) => ({
			...prevData,
			selectedDays,
		}));
	};

	const handleSaveChanges = () => {
		if (selectedAgentId) {
			mutation.mutate(formData);
		} else {
			message.error('No campaign selected');
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64">
				<Spin size="large" tip="Loading schedule settings..." />
			</div>
		);
	}

	return (
		<>
			<div className="bg-white p-6 rounded-lg">
				<div className="flex justify-between items-center mb-4">
					{renderStatusBadge(campaignSchedule?.schedule?.status as string)}
					{renderScheduleControls()}
				</div>
				<Form>
					{/* Time Zone Selection */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-2">Time Zone</label>

						<Select
							showSearch
							value={formData.timezone}
							placeholder="Select a time zone"
							style={{ width: '100%' }}
							onChange={handleTimeZoneChange}
							options={timezones.map((zone) => ({ label: zone, value: zone }))}
							filterOption={(input, option) =>
								(option?.label as string).toLowerCase().includes(input.toLowerCase())
							}
						/>
					</div>

					{/* Schedule Type Selection */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
						<div className="space-y-3">
							<Row gutter={[6, 6]}>
								{[
									{
										type: 'business',
										icon: <ClockCircleOutlined className="text-lg" />,
										label: 'Business Hours',
										desc: 'Monday-Friday, 9 AM - 5 PM',
									},
									{
										type: 'extended',
										icon: <FieldTimeOutlined className="text-lg" />,
										label: 'Extended Hours',
										desc: 'Monday-Friday, 8 AM - 8 PM',
									},
									{
										type: 'custom',
										icon: <CalendarOutlined className="text-lg" />,
										label: 'Custom Schedule',
										desc: 'Choose your own days and times',
									},
								].map(({ type, icon, label, desc }) => (
									<Col lg={8} md={24} key={type}>
										<label
											className={`block p-4 border rounded-lg cursor-pointer ${formData.scheduleType === type ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
										>
											<div className="flex items-center">
												<input
													type="radio"
													name="scheduleType"
													value={type}
													checked={formData.scheduleType === type}
													onChange={() => handleRadioChange('scheduleType', type)}
													style={{ display: 'none' }}
												/>
												<div
													className={`w-6 h-6 mr-3 ${formData.scheduleType === type ? 'text-blue-500' : 'text-gray-500'}`}
												>
													{icon}
												</div>
												<div>
													<div className="font-medium">{label}</div>
													<div className="text-sm text-gray-500">{desc}</div>
												</div>
											</div>
										</label>
									</Col>
								))}
							</Row>
						</div>
					</div>

					{/* Custom Schedule Options */}
					{formData.scheduleType === 'custom' && (
						<div className="mb-6">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Select Days{' '}
								<Tooltip title="Days when calls are allowed">
									<QuestionCircleOutlined />
								</Tooltip>
							</label>
							<Select
								mode="multiple"
								allowClear
								placeholder="Please select"
								value={formData.selectedDays}
								onChange={handleDaySelection}
								className="w-full"
								options={daysOfWeek.map((day) => ({ label: day, value: day }))}
							/>

							{/* Start and End Time */}
							<Row gutter={8}>
								<Col lg={12} sm={24}>
									<div className="mt-4">
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Start Time{' '}
											<Tooltip title="Daily call start time">
												<QuestionCircleOutlined />
											</Tooltip>
										</label>
										<input
											type="time"
											name="startTime"
											value={formData.startTime}
											onChange={handleInputChange}
											className="w-full p-2 border border-gray-300 rounded-md"
										/>
									</div>
								</Col>
								<Col lg={12} sm={24}>
									<div className="mt-4">
										<label className="block text-sm font-medium text-gray-700 mb-2">
											End Time{' '}
											<Tooltip title="Daily call end time">
												<QuestionCircleOutlined />
											</Tooltip>
										</label>
										<input
											type="time"
											name="endTime"
											value={formData.endTime}
											onChange={handleInputChange}
											className="w-full p-2 border border-gray-300 rounded-md"
										/>
									</div>
								</Col>
							</Row>
						</div>
					)}
					<div className="mb-6">
						<Row gutter={8}>
							<Col lg={12} sm={24}>
								<div className="mt-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Maximum Retries{' '}
										<Tooltip title="Number of times to attempt reaching a contact">
											<QuestionCircleOutlined />
										</Tooltip>
									</label>
									<input
										type="number"
										name="maximumRetries"
										value={formData.maximumRetries}
										onChange={handleInputChange}
										className="w-full p-2 border border-gray-300 rounded-md"
									/>
								</div>
							</Col>
							<Col lg={12} sm={24}>
								<div className="mt-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Hours Between Retries{' '}
										<Tooltip title="Minimum waiting period between attempts">
											<QuestionCircleOutlined />
										</Tooltip>
									</label>
									<input
										type="number"
										name="hoursBetweenRetries"
										value={formData.hoursBetweenRetries}
										onChange={handleInputChange}
										className="w-full p-2 border border-gray-300 rounded-md"
									/>
								</div>
							</Col>
						</Row>
					</div>

					{/* Maximum Calls Per Day */}
					<div className="mb-6">
						<label className="text-sm font-medium text-gray-700 mb-2">
							Maximum calls per day: {formData.maxCallsPerDay}
						</label>
						<input
							type="range"
							min="20"
							max="100"
							step="1"
							value={formData.maxCallsPerDay}
							onChange={handleSliderChange}
							className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
						/>
						<div className="flex justify-between text-xs text-gray-500 mt-1">
							<span>20 calls</span>
							<span>100 calls</span>
						</div>
					</div>
					<div className="p-4 bg-yellow-50 border border-yellow-100 rounded-md mb-6">
						<div className="flex">
							<div className="text-yellow-600 mr-3">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-6 w-6"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									/>
								</svg>
							</div>
							<div className="text-sm text-yellow-700">
								We recommend starting with a lower volume to test your campaign's effectiveness. You
								can always increase volume later.
							</div>
						</div>
					</div>
					<div className="text-end">
						<Button
							icon={<SaveOutlined />}
							type="primary"
							size="large"
							disabled={!isFormChanged || mutation.isPending || isRefetching}
							onClick={handleSaveChanges}
							loading={mutation.isPending}
						>
							{mutation.isPending ? 'Saving...' : 'Save Changes'}
						</Button>
					</div>
				</Form>
			</div>
			{/* Overlay spinner for refetching data */}
			{isRefetching && (
				<div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
					<Spin size="large" tip="Refreshing data..." />
				</div>
			)}
		</>
	);
};

export default ScheduleSettings;
